import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, Search } from 'lucide-react';
import { getAllItemTermHeads, getTermRemarksByHead } from '../../api/SupplierPOAPI/supplierPOCreationAPI';

/**
 * Legacy "Predefined Terms Exist?" block (Views/Purchase/TermRemaksView*.cshtml), shared by the SPPO pages.
 * value = { exist: '' | 'Yes' | 'No', headId: '', lines: [] }  — lines become PreferredRemarks ("t1|t2|").
 */
const headIdOf = (h) => h.HeadId || h.headId || h.ID || h.ItemId;
const headNameOf = (h) => h.HeadName || h.headName || h.Name || h.ItemHeadName;

export const preferredRemarksOf = (lines) => (lines || []).map((t) => `${String(t).trim().replaceAll('|', '')}|`).join('');

const PredefinedTermsPicker = ({ value, onChange, disabled = false, selectCls }) => {
    const [heads, setHeads] = useState([]);
    const [loadingHeads, setLoadingHeads] = useState(false);
    const [loadingTerms, setLoadingTerms] = useState(false);
    const cls = selectCls || 'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700';

    useEffect(() => {
        if (value.exist !== 'Yes' || heads.length) return;
        setLoadingHeads(true);
        getAllItemTermHeads()
            .then((res) => { const d = res?.data?.Data ?? res?.data ?? []; setHeads(Array.isArray(d) ? d : []); })
            .catch(() => toast.error('Failed to load term heads'))
            .finally(() => setLoadingHeads(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.exist]);

    const loadTerms = async () => {
        const head = heads.find((h) => String(headIdOf(h)) === String(value.headId));
        if (!head) { toast.warn('Select Predefined Terms Head Name'); return; }
        setLoadingTerms(true);
        try {
            const res = await getTermRemarksByHead(headNameOf(head), headIdOf(head));
            const d = res?.data?.Data ?? res?.data ?? [];
            const lines = (Array.isArray(d) ? d : []).map((r) => r.TermRemark || r.Remark || r.Description || String(r)).filter(Boolean);
            onChange({ ...value, lines });
        } catch { toast.error('Failed to load predefined terms'); }
        finally { setLoadingTerms(false); }
    };

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                        Predefined Terms Exist?<span className="text-rose-500 ml-0.5">*</span>
                    </label>
                    <select className={cls} value={value.exist} disabled={disabled}
                        onChange={(e) => onChange({ exist: e.target.value, headId: '', lines: [] })}>
                        <option value="">-Please Select-</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                </div>
                {value.exist === 'Yes' && (
                    <>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Terms Head<span className="text-rose-500 ml-0.5">*</span>
                            </label>
                            <select className={cls} value={value.headId} disabled={disabled || loadingHeads}
                                onChange={(e) => onChange({ ...value, headId: e.target.value, lines: [] })}>
                                <option value="">{loadingHeads ? 'Loading…' : '-Please Select-'}</option>
                                {heads.map((h) => <option key={headIdOf(h)} value={headIdOf(h)}>{headNameOf(h)}</option>)}
                            </select>
                        </div>
                        <div>
                            <button type="button" onClick={loadTerms} disabled={disabled || loadingTerms || !value.headId}
                                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loadingTerms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Load Terms
                            </button>
                        </div>
                    </>
                )}
            </div>
            {value.exist === 'Yes' && value.lines.length > 0 && (
                <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1">
                    {value.lines.map((t, i) => <li key={i}>{t}</li>)}
                </ol>
            )}
        </div>
    );
};

export default PredefinedTermsPicker;
